import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { Theme } from '@mui/material'
import { Box, css, Stack, styled, Typography, useTheme, useMediaQuery } from '@mui/material'
import type { FC, ReactNode } from 'react'
import { A11y as SwiperA11y, Navigation as SwiperNavigation, Pagination as SwiperPagination } from 'swiper/modules'
import { Swiper as SwiperReact, SwiperSlide } from 'swiper/react'

import { COLORS, ICONS } from '../../../constants'
import { hideEmptyCss } from '../../../styles'
import { unit, unitless } from '../../../utils'

import { useSwiperRef } from './useSwiperRef'

import 'swiper/swiper-bundle.css'

export interface CarouselProps {
  children: [string, ReactNode][]
  slidesMargin: string
  title: ReactNode
}

export const Carousel: FC<CarouselProps> = ({ children, slidesMargin, title }) => {
  const [nextEl, nextElRef] = useSwiperRef<HTMLButtonElement>()
  const [prevEl, prevElRef] = useSwiperRef<HTMLButtonElement>()
  const [paginationEl, paginationRef] = useSwiperRef<HTMLDivElement>()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const finalPaginationType = isMobile && children.length > 20 ? 'fraction' : 'bullets'

  return (
    <Box marginY={unit(2)} css={swiperCss(theme)}>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent={isMobile ? 'space-between' : 'start'}
        marginBottom={unit(5)}
        marginX={unit(4.5)}
        gap={unit(18)}
      >
        <Typography variant='headlineM'>{title}</Typography>
        <Stack direction='row' gap={unit(6)}>
          <ButtonNavigation ref={prevElRef} aria-label='previous'>
            <FontAwesomeIcon icon={ICONS.farArrowLeft} fontSize={unit(6)} />
          </ButtonNavigation>
          <ButtonNavigation ref={nextElRef} aria-label='next'>
            <FontAwesomeIcon icon={ICONS.farArrowRight} fontSize={unit(6)} />
          </ButtonNavigation>
        </Stack>
      </Stack>
      <Box marginRight={`-${slidesMargin}`}>
        <SwiperReact
          key={`${finalPaginationType}-${isMobile}`}
          modules={[SwiperNavigation, SwiperPagination, SwiperA11y]}
          spaceBetween={unitless(2)}
          slidesPerView='auto'
          navigation={{
            prevEl,
            nextEl,
          }}
          a11y={{ enabled: true }}
          pagination={{
            clickable: true,
            el: paginationEl,
            type: finalPaginationType,
            bulletElement: 'button',
          }}
        >
          {children.map(([key, node]) => (
            <SwiperSlide key={key}>{node}</SwiperSlide>
          ))}
        </SwiperReact>
      </Box>
      <Footer css={hideEmptyCss} ref={paginationRef} />
    </Box>
  )
}

const swiperCss = (theme: Theme) => css`
  .swiper-slide {
    width: auto;
  }

  .swiper-pagination-bullet {
    opacity: unset;
    background-color: ${COLORS.burgundi[300]};
    width: ${unit(2)};
    height: ${unit(2)};
    margin: 0 ${unit(1)};
  }

  .swiper-pagination-bullet-active {
    opacity: unset;
    background-color: ${COLORS.semantic.floatingLabel};
  }

  .swiper-pagination-fraction {
    font-size: ${unit(3.5)};
    color: ${COLORS.semantic.floatingLabel};
    font-weight: 600;
    background: ${COLORS.white};
    border: 2px solid ${COLORS.burgundi[300]};
    border-radius: ${unit(12)};
    padding: ${unit(1.5)} ${unit(3)};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    justify-self: center;
    justify-content: center;
    width: max-content;
    min-width: auto;
    height: ${unit(6)};

    .swiper-pagination-current {
      color: ${COLORS.semantic.floatingLabel};
      font-weight: 700;
    }

    .swiper-pagination-total {
      color: ${COLORS.grey['700']};
      font-weight: 500;
    }
  }

  ${theme.breakpoints.down('md')} {
    .swiper-pagination-bullets {
      &.swiper-pagination-bullets-dynamic {
        display: none;
      }
    }

    .swiper-pagination-fraction {
      font-size: ${unit(4)};
      padding: ${unit(2)} ${unit(4)};
      height: ${unit(8)};
      border-radius: ${unit(12)};
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }
`

const ButtonNavigation = styled('button')`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: ${COLORS.black};

  &:disabled {
    color: ${COLORS.semantic.disabled};
  }
`

const Footer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: ${unit(5)};
  width: 100%;
`
